import { ProductStatus } from "@/admin/experiences/__generated__/AdminExperiencesListTableRowFragment.graphql"
import { ProductRegistrationAvailability } from "@/content-usage/__generated__/ContentUsageInstanceItemQuery.graphql"
import { useLabel } from "@/core/context/LabelsContext"
import FormStore from "@/core/form/store/FormStore"
import { ProductSettingsFormSaveButton_ProductFragment$key } from "@/product/settings/__generated__/ProductSettingsFormSaveButton_ProductFragment.graphql"
import { useAdminProductLabel } from "@/product/util/hook/useProductLabel"
import Relay from "@/relay/relayUtils"
import Form from "@components/form/Form"
import { DiscoButton, DiscoButtonSkeleton, DiscoDropdown, DiscoText } from "@disco-ui"
import DiscoButtonsGroup from "@disco-ui/button/DiscoButtonsGroup"
import DiscoDropdownItem from "@disco-ui/dropdown/DiscoDropdownItem"
import { TestIDProps } from "@utils/typeUtils"
import { observer } from "mobx-react-lite"
import { graphql, useFragment } from "react-relay"
import classes from "./ProductSettingsFormSaveButton.module.css"

interface Props extends TestIDProps {
  form: FormStore<{
    status?: ProductStatus | null
    registrationAvailability?: ProductRegistrationAvailability | null
  }>
  productKey: ProductSettingsFormSaveButton_ProductFragment$key
}

const ProductSettingsFormSaveButton = ({
  form,
  productKey,
  testid = "ProductSettingsFormSaveButton",
}: Props) => {
  const product = useFragment<ProductSettingsFormSaveButton_ProductFragment$key>(
    graphql`
      fragment ProductSettingsFormSaveButton_ProductFragment on Product {
        type
        isInPublishedPathway
      }
    `,
    productKey
  )

  const productLabel = useAdminProductLabel(product.type)
  const pathwayLabel = useAdminProductLabel("pathway")
  const membersLabel = useLabel("admin_member")
  const isPublishing = form.state.status === "published"

  const disabledReason = getDisabledReason()

  return (
    <div>
      <DiscoDropdown
        testid={testid}
        shouldDisplaySpinner={form.isSubmitting}
        menuClasses={{ paper: classes.menuPaper }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        disabled={!form.isChanged || form.isSubmitting}
        menuButton={({ onClick, disabled, shouldDisplaySpinner }) => (
          <DiscoButtonsGroup
            noPadding
            buttons={[
              {
                uniqueKey: "submit-button",
                ...(disabledReason ? { TooltipProps: { content: disabledReason } } : {}),
                content: (
                  <Form.SubmitButton
                    form={form}
                    testid={`${testid}.submit-button`}
                    disabled={Boolean(disabledReason) || disabled}
                    shouldDisplaySpinner={shouldDisplaySpinner}
                  >
                    {isPublishing ? "Publish Changes" : "Save as Draft"}
                  </Form.SubmitButton>
                ),
              },
              {
                uniqueKey: "dropdown-button",
                content: (
                  <DiscoButton
                    testid={`${testid}.dropdown-button`}
                    className={classes.iconButton}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      onClick(e)
                    }}
                    leftIcon={"arrow-down"}
                  />
                ),
              },
            ]}
          />
        )}
      >
        {isPublishing ? (
          <DiscoDropdownItem
            testid={`${testid}.save-draft`}
            title={"Save as Draft"}
            subtitle={
              <DiscoText
                variant={"body-sm"}
                color={"text.secondary"}
                className={classes.menuItemSubtitle}
              >
                {`Continue editing worry free. Draft ${productLabel.plural} are only visible to admins.`}
              </DiscoText>
            }
            onClick={() => (form.state.status = "draft")}
            className={classes.menuItem}
          />
        ) : (
          <DiscoDropdownItem
            testid={`${testid}.publish`}
            title={`Publish ${productLabel.singular}`}
            subtitle={
              <DiscoText
                variant={"body-sm"}
                color={"text.secondary"}
                className={classes.menuItemSubtitle}
              >
                {form.state.registrationAvailability === "hidden"
                  ? `${membersLabel.plural} can be invited and register for this ${productLabel.singular}.`
                  : `${membersLabel.plural} can discover, register or be assigned this ${productLabel.singular}.`}
              </DiscoText>
            }
            onClick={() => {
              form.state.status = "published"
            }}
            className={classes.menuItem}
          />
        )}
      </DiscoDropdown>
    </div>
  )

  function getDisabledReason() {
    if (product.isInPublishedPathway && !isPublishing)
      return `Cannot change ${productLabel.singular} status back to Draft because this ${productLabel.singular} is part of a published ${pathwayLabel.singular}.`
    return undefined
  }
}

export default Relay.withSkeleton({
  component: observer(ProductSettingsFormSaveButton),
  skeleton: () => <DiscoButtonSkeleton />,
})
